// Pinata uploads API endpoint
// Docs: https://docs.pinata.cloud
const PINATA_UPLOAD_URL =
  'https://uploads.pinata.cloud/v3/files';

// JWT for Pinata. This is safe to expose in the frontend
// (it should be scoped appropriately in your Pinata dashboard).
// Define in .env as REACT_APP_PINATA_JWT (CRA requires the REACT_APP_ prefix
// for env vars to be exposed to the browser bundle).
const PINATA_JWT =
  process.env.REACT_APP_PINATA_JWT || process.env.PINATA_JWT;

/** Default matches typical backend `ipfsGateway()` when unset. */
const DEFAULT_IPFS_GATEWAY = 'https://gateway.pinata.cloud/ipfs/';

/**
 * HTTP gateway URL for a CID — same shape as Go `joinGatewayCID(ipfsGateway(), cid)`.
 * Set `REACT_APP_IPFS_GATEWAY` to match your server `config.ipfs.gateway` when debugging fetches.
 *
 * @param {string} cid
 * @returns {string}
 */
export function ipfsGatewayUrlForCid(cid) {
  let gw = (
    process.env.REACT_APP_IPFS_GATEWAY ||
    DEFAULT_IPFS_GATEWAY
  ).trim();
  if (!gw) {
    gw = DEFAULT_IPFS_GATEWAY;
  }
  if (!gw.endsWith('/')) {
    gw += '/';
  }
  return gw + String(cid).replace(/^\//, '');
}

/**
 * Upload a JSON-serializable object to IPFS and return its CID (as a string).
 * The object will be stored as a UTF-8 JSON blob.
 *
 * @param {any} data - JSON-serializable data to store.
 * @returns {Promise<string>} The CID string.
 */
export async function uploadJsonToIpfs(data) {
  if (!PINATA_JWT) {
    throw new Error('PINATA_JWT is not set');
  }

  const json = JSON.stringify(data);
  const blob = new Blob([json], { type: 'application/json' });

  const form = new FormData();
  form.append('file', blob, 'proof.json');
  form.append('network', 'public');
  form.append('name', 'zk-proof');

  const res = await fetch(PINATA_UPLOAD_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${PINATA_JWT}`,
    },
    body: form,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Pinata upload failed: ${text}`);
  }

  const jsonRes = await res.json();
  const cid = jsonRes?.data?.cid?.toString();
  if (cid) {
    console.log(
      '[IPFS] Uploaded JSON — gateway URL:',
      ipfsGatewayUrlForCid(cid)
    );
  }
  return cid;
}

/**
 * Upload raw bytes to IPFS (Pinata) and return the CID.
 *
 * @param {Uint8Array|ArrayBuffer} bytes - Raw proof bytes.
 * @param {string} [name='proof.bin'] - Optional file name metadata.
 * @returns {Promise<string>} The CID string.
 */
export async function uploadBytesToIpfs(bytes, name = 'proof.bin') {
  if (!PINATA_JWT) {
    throw new Error('PINATA_JWT is not set');
  }

  const uint8 =
    bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  const blob = new Blob([uint8], { type: 'application/octet-stream' });

  const form = new FormData();
  form.append('file', blob, name);
  form.append('network', 'public');
  form.append('name', name);

  const res = await fetch(PINATA_UPLOAD_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${PINATA_JWT}`,
    },
    body: form,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Pinata upload failed: ${text}`);
  }

  const jsonRes = await res.json();
  const cid = jsonRes?.data?.cid?.toString();
  if (cid) {
    console.log(
      '[IPFS] Uploaded proof bytes — gateway URL (what the server GETs):',
      ipfsGatewayUrlForCid(cid)
    );
  }
  return cid;
}

